import {
  CascadingFilterInput,
  CascadingFilterInputOption,
  isNotNil,
} from '@wrisk/ui-components'
import { uniq } from 'lodash-es'
import React, { FunctionComponent, useEffect, useMemo } from 'react'

import {
  toBodyTypeKBA,
  toFuelTypeKBA,
  toGenerationDescriptionKBA,
  toKBAWithYear,
  toPerformanceKBA,
  VehicleKBA,
} from '../../../../../domain'
import { useApiErrorHandlingAsync } from '../../../../../hooks/auth'
import {
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { usePrincipal } from '../../../../authentication'
import { WriskTrans } from '../../../trans'

interface VehicleResultsFilterProps {
  onChange: (value: VehicleKBA | null) => void
  value?: VehicleKBA | null
  profile?: string
  year: number
  make: string
  model: string
  tKey: TKeyBuilder
}

export const VehicleResultsFilter: FunctionComponent<VehicleResultsFilterProps> = ({
  onChange,
  profile,
  year,
  make,
  model,
  value,
  tKey,
}) => {
  const { t } = useWriskTranslation()

  const { apiClient } = usePrincipal()

  const config = useMemo(
    () =>
      Object.entries({
        fuel: toFuelTypeKBA(t),
        performance: toPerformanceKBA,
        generationDescription: toGenerationDescriptionKBA,
        bodyType: toBodyTypeKBA,
        kbaWithYear: toKBAWithYear(t),
      }).map(([id, expression]) => ({
        id,
        placeholder: t(tKey('placeholders', id)),
        mapItemsToOptions: (vehicles: VehicleKBA[]) =>
          uniq(vehicles.map(expression))
            .filter(isNotNil)
            .toSorted()
            .map((id: string) => ({ id, text: id })),
        matchItemToOption:
          ({ id }: CascadingFilterInputOption) =>
          (vehicle: VehicleKBA) =>
            expression(vehicle) === id,
        matchOptionToItem:
          (vehicle: VehicleKBA) =>
          ({ id }: CascadingFilterInputOption) =>
            expression(vehicle) === id,
      })),
    [t],
  )

  const { result } = useApiErrorHandlingAsync(
    async () =>
      await apiClient.lookupVehicleSearch(
        {
          year,
          make,
          model,
        },
        profile,
      ),
    [apiClient, profile],
  )

  useEffect(() => {
    if (result?.length === 1) {
      onChange(result[0])
    }
  }, [result, onChange])

  return result ? (
    <CascadingFilterInput
      value={value}
      items={result}
      onSelect={onChange}
      config={config}
      errorMessage={<WriskTrans t={t} i18nKey={tKey('errors', 'no_results')} />}
    />
  ) : null
}
